import { Component, OnInit, Output, EventEmitter, Input, ViewChild,
    OnChanges, SimpleChanges, ViewChildren, ViewContainerRef, QueryList } from '@angular/core';

import { DynamicComponent, GenericValue, HisNotificationService, HisUiService } from 'hisng';

import { DYNAMIC_FIELD_ANCHOR } from 'app/fs/res/common/model/dynamic-content/constants';
import { ResearchPartnerSimple } from 'app/fs/res/common/model/researchPartner/researchPartner';
import { ProviderManager } from 'app/fs/res/common/utils/provider-manager';

import { CREATOR_EDIT, CREATOR_ADD, CREATOR_KEY, ORIGINAL_CREATOR_KEY } from 'app/fs/res/publication/model/constants';
import { PublicationCreatorWizardComponent } from './creator-wizard/publication-creator-wizard.component';
import { CreatorType } from 'app/fs/res/publication/model/enums/creator-type-enum';
import { PublicationCreatorOrgunit } from 'app/fs/res/publication/model/publication-creator-orgunit';
import { PublicationCreator } from 'app/fs/res/publication/model/publication-creator';

@Component( {
    selector: 'res-publication-creator-panel',
    templateUrl: './publication-creator-panel.component.html',
    providers: [ProviderManager.getComponentProvider(PublicationCreatorPanelComponent)],

} )
export class PublicationCreatorPanelComponent implements OnInit, OnChanges {
    // eslint-disable-next-line no-underscore-dangle
    @Input( 'creators' ) set _creators( creators: PublicationCreator[] ) {
        if ( creators?.length ) {
            this.creators = creators;
            this.personcreatorsTemp = creators.filter( ( c ) => c.firstname );
        }
    }
    @Input() creatorType: GenericValue;
    @Input() dynamicComponent: DynamicComponent;
    @Output() creatorsChange = new EventEmitter();
    @Output() creatorTypeChange = new EventEmitter();
    @ViewChild( PublicationCreatorWizardComponent ) creatorWizard: PublicationCreatorWizardComponent;
    @ViewChildren( DYNAMIC_FIELD_ANCHOR, { read: ViewContainerRef } ) fieldViewContainerList: QueryList<ViewContainerRef>;

    // interne/externe Orgs als Koerperschaften
    public internalOrganizations: GenericValue[] = [];
    public externalOrganizations: ResearchPartnerSimple[] = [];

    public visibility = false;
    public isAddCreator = false;

    creators: PublicationCreator[] = [];
    selectedCreators: any[] = []; // selected creators to be marked in search-select // XXX currently unused
    personcreatorsTemp: PublicationCreator[] = [];
    orgcreatorsTemp: PublicationCreator[] = [];

    constructor(
        private notificationService: HisNotificationService,
        private hisUiService: HisUiService ) {
    }

    ngOnInit() {
        if ( !this.creators ) {
            this.creators = [];
        }
        localStorage.removeItem( CREATOR_KEY );
        localStorage.removeItem( ORIGINAL_CREATOR_KEY );
    }

    /* Component ngOnChanges() */
    ngOnChanges( changes: SimpleChanges ) {
        if ( changes ) {
            this.setInternalOrganizations();
            this.setExternalOrganizations();
            if ( this.isTypeOfCreators() ) {
                // set already assigned options for generic-search-select component
                this.setSelectedCreators();
            }
        }
    }

    // getter ###################################################################################

    isTypeOfEditor() {
        return this.creatorType && CreatorType.editorsAsPerson().findIndex( c => c === this.creatorType.hiskeyId ) > -1;
    }

    isTypeOfAuthor(): boolean {
        return this.creatorType && CreatorType.authorsAsPerson().findIndex( c => c === this.creatorType.hiskeyId ) > -1;
    }

    isTypeOfCreators() {
        return this.creatorType && CreatorType.creators().findIndex( c => c === this.creatorType.hiskeyId ) > -1;
    }

    isTypeOfOrganisation() {
        return this.creatorType && CreatorType.orgas().findIndex( c => c === this.creatorType.hiskeyId ) > -1;
    }

    // button/link methods ################################################################################################

    /**
     * open wizard to input data of new creator
     */
    addCreator() {
        this.creatorWizard.showDialog();
        this.creatorWizard.creatorType = this.creatorType;
        localStorage.setItem( CREATOR_KEY, CREATOR_ADD );
    }

    /**
     * open wizard to edit data of given creator
     *
     * @param creator PublicationCreator the creator which data is to be edited
     */
    editCreator( creator: PublicationCreator ) {
        this.creatorWizard.showDialog();
        // deep copy of creator to prevent changes in source creator (should only be applied after button click in wizard)
        this.creatorWizard.creator = Object.assign( {}, creator );
        this.creatorWizard.creatorType = this.creatorType;
        localStorage.setItem( CREATOR_KEY, CREATOR_EDIT );
        localStorage.setItem( ORIGINAL_CREATOR_KEY, JSON.stringify( creator ) );
    }

    /**
     * move list entry (creator) up
     *
     * @param creator PublicationCreator to move up
     */
    moveUp( creator: PublicationCreator ) {
        let newIndex = this.creators.findIndex( c => c.sortorder === ( creator.sortorder - 1 ) );
        if ( newIndex >= 0 ) {
            creator.sortorder = creator.sortorder - 1;
            this.creators[newIndex].sortorder = this.creators[newIndex].sortorder + 1;
        } else {
            this.notificationService.info(
                this.hisUiService.translate(
                    'fs.res.publication.information.creatorTypeHigher',
                    this.creatorType.text,
                    creator.creatorname ),
                this.hisUiService.translate( 'view.images.information' )
            );
        }
        this.creators = this.creators.sort( ( a, b ) => a.sortorder - b.sortorder );
        this.creatorsChange.emit( this.creators );
    }

    /**
     * move list entry (creator) down
     *
     * @param creator PublicationCreator to move down
     */
    moveDown( creator: PublicationCreator ) {
        let newIndex = this.creators.findIndex( c => c.sortorder === ( creator.sortorder + 1 ) );
        if ( newIndex > 0 && newIndex < this.creators.length ) {
            creator.sortorder = creator.sortorder + 1;
            this.creators[newIndex].sortorder = this.creators[newIndex].sortorder - 1;
        } else {
            this.notificationService.info(
                this.hisUiService.translate(
                    'fs.res.publication.information.creatorTypeLower',
                    this.creatorType.text, creator.creatorname ),
                this.hisUiService.translate( 'view.images.information' )
            );
        }
        this.creators = this.creators.sort( ( a, b ) => a.sortorder - b.sortorder );
        this.creatorsChange.emit( this.creators );
    }

    /**
     * remove creator from creator list
     *
     * @param index index of creator to delete from list
     */
    deleteCreator( index: number ) {
        let newSortorder = 1;
        this.creators.splice( index, 1 );
        // set new sortorder for creators
        this.creators.forEach( c => { c.sortorder = newSortorder; newSortorder++; } );
        this.notificationService.infoI18n( 'de.his.javang.gefunden.change' );
        this.creatorsChange.emit( this.creators );
    }

    getEditOfType() {
        return this.creatorType.hiskeyId === 2
            ? this.hisUiService.translate('fs.res.publication.editor') : this.hisUiService.translate('fs.res.publication.author');
    }

    // data manipulation methods ###############################################################

    /**
     * applies the changed data from the wizard
     *
     * @param event the changed creatorData
     */
    applyChangedCreator( event: PublicationCreator ) {
        if ( !event.sortorder ) {
            event.sortorder = this.creators.length + 1;
            this.creators.push( event );
        } else {   
	        let index = this.creators.findIndex( creator => creator.sortorder === event.sortorder );
	        this.creators[index] = event;              
        }
        this.notificationService.infoI18n( 'de.his.javang.gefunden.change' );
        this.creatorsChange.emit( this.creators );
    }

    selectCreatorType( event: GenericValue ) {
        this.creatorType = event;
        this.creatorTypeChange.emit( this.creatorType );
        if ( CreatorType.orgas().indexOf( this.creatorType.hiskeyId ) >= 0 ) {
            this.personcreatorsTemp = this.creators.filter( ( c ) => c.firstname );
            this.creators = this.orgcreatorsTemp ? this.orgcreatorsTemp : [];
        } else {
            this.orgcreatorsTemp = this.creators.filter( ( c ) => !c.firstname );
            this.creators = this.personcreatorsTemp ? this.personcreatorsTemp : [];
        }
        this.creatorsChange.emit( this.creators );
    }

    // methods for data manipulation if creatorType is organization ##############################

    /* show pop-up dialog to create new researchPartner (External organization unit) */
    createExternalOrgUnitChange() {
        this.visibility = true;
    }

    /**
     * event handler implementation of selected internal/external organization
     *
     * @param event GenericValue[] as external organization units to set on patent
     */
    internalOrganizationsChange( event: GenericValue[] ): void {
        if ( this.creators == null ) {
            this.creators = new Array<PublicationCreator>();
        }

        // remove all creators with internal organisations from this.creators
        this.creators = this.creators.filter( creator => {
            creator.creatorOrganizations.filter( organization => organization.researchPartner !== null );
            return !this.creatorContainsOrgunit( creator );
        } );

        // add all internal organisations that are currently selected to this.creators
        event.forEach( ( genericValueOrgUnit, index ) => {
            let publicationCreatorOrgunit = new PublicationCreatorOrgunit( null, null, genericValueOrgUnit, null, null );
            this.creators = [
                ...this.creators,
                new PublicationCreator( null, genericValueOrgUnit.text, null, null, [publicationCreatorOrgunit], index )];
        } );
        this.creatorsChange.emit( this.creators );
        this.isAddCreator = false;
    }

    /**
     * call back method implementation of deleteOrganizationChange
     *
     * @param event GenericValue | ResearchPartnerSimple
     */
    deleteOrganizationChange( event: GenericValue | ResearchPartnerSimple ) {
        let index = this.creators.findIndex( creator => creator.creatorOrganizations &&
            creator.creatorOrganizations.find( creatorOrgunit =>
                creatorOrgunit.organization && creatorOrgunit.organization.id === event.id
                || creatorOrgunit.researchPartner && creatorOrgunit.researchPartner.id === event.id ) !== undefined );
        this.creators.splice( index, 1 );
        this.creatorsChange.emit( [...this.creators] );
    }

    /**
     * event handler implementation of selected external organization units
     *
     * @param isExtern boolean if organization is internal or external
     */
    externalOrganizationsChange( event: ResearchPartnerSimple[] ): void {
        if ( this.creators == null ) {
            this.creators = new Array<PublicationCreator>();
        }

        // remove all creators with external organisations from this.creators
        this.creators = this.creators.filter( creator => {
            creator.creatorOrganizations.filter( organization => organization.researchPartner == null );
            return !this.creatorContainsOrgunit( creator, true );
        } );

        // add all external organisations that are currently selected to this.creators
        event.forEach( ( researchPartner, index ) => {
            let publicationCreatorOrgunit = new PublicationCreatorOrgunit( null, null, null, researchPartner, null );
            this.creators = [
                ...this.creators,
                new PublicationCreator( null, researchPartner.defaulttext, null, null, [publicationCreatorOrgunit], index )];
        } );
        this.creatorsChange.emit( this.creators );
        this.isAddCreator = false;
    }

    /* set filtered Array of internal organizations */
    setInternalOrganizations(): GenericValue[] {
        this.internalOrganizations = [];
        if ( this.creators ) {
            this.creators.forEach( ( creator, index ) => {
                if ( creator && creator.creatorOrganizations != null ) {
                    if ( creator.sortorder == null ) {
                        creator.sortorder = this.creators.length + index;
                    }
                    creator.creatorOrganizations.forEach( creatorOrganization => {
                        if ( creatorOrganization.organization ) {
                            this.internalOrganizations.push( creatorOrganization.organization );
                        }
                    } );
                }
            } );
        }
        return this.internalOrganizations;
    }

    /* set filtered Array of external organizations */
    setExternalOrganizations(): ResearchPartnerSimple[] {
        this.externalOrganizations = [];
        if ( this.creators ) {
            this.creators.forEach( ( creator, index ) => {
                if ( creator && creator.creatorOrganizations != null ) {
                    if ( creator.sortorder == null ) {
                        creator.sortorder = this.creators.length + index;
                    }
                    creator.creatorOrganizations.forEach( creatorOrganization => {
                        if ( creatorOrganization.researchPartner ) {
                            this.externalOrganizations.push( creatorOrganization.researchPartner );
                        }
                    } );
                }
            } );
        }
        return this.externalOrganizations;
    }

    /* set visibility of select-box and assign-button */
    setIsAddCreator() {
        this.isAddCreator = true;
    }

    /**
     * set pre-selected creators for his-search-select
     */
    setSelectedCreators() {
        this.selectedCreators = [];
        if ( this.creators ) {
            this.creators.forEach( creator => {
                // Ein Autor, der schon ausgewählt wurde, muss auch ein person-Objekt haben.
                // Im Umkehrschluss können Autoren ohne person-Objekt auch noch nicht ausgewählt sein.
                if ( creator.person ) {
                    // Damit nur der Eintrag mit der richtigen Orgunit im Suchergebnis
                    // gehighlighted wird, brauchen wir Person.id + Orgunit.id als id #224172
                    //        let personOrgunitId = creator.person.id + '.' + creator.organization.id;
                    //      this.selectedCreators.push( { id: personOrgunitId } );
                }
            } );
        }
    }

    /* set new value of researchPartner */
    saveDialogChange( event: ResearchPartnerSimple ) {
        let publicationCreatorOrgunit = new PublicationCreatorOrgunit( null, null, null, event, null );
        this.creators = [
            ...this.creators,
            new PublicationCreator( null, event.defaulttext, null, null, [publicationCreatorOrgunit], this.creators.length + 1 )];
        this.setExternalOrganizations();
        this.creatorsChange.emit( this.creators );
        this.visibility = false;
        this.isAddCreator = false;
    }

    creatorContainsOrgunit( creator: PublicationCreator, isExternal: boolean = false ): boolean {
        for ( let creatorOrgunit of creator.creatorOrganizations ) {
            if ( !isExternal && creatorOrgunit.organization !== null ) {
                return true;
            } else if ( isExternal && creatorOrgunit.researchPartner !== null ) {
                return true;
            }
        }
        return false;
    }

    hasOrcidId(creator: PublicationCreator): boolean {
        if(creator.orcid != null){
            if(creator.orcid.length !== 0) {
                return true;
            }else {
                return false;
            }
        }
        return false;
    }
}
